import Helmet from '../lib/Helmet';
import { MapPin, Phone, Linkedin, ShieldCheck, Trophy } from 'lucide-react';
import { awards, clients } from '../mock';
import { track, FUNNEL } from '../lib/track';

// Route: /about  (top of the burger menu, linked from every product page)
//
// Built for one customer worry: "anyone can make a website with AI, are these
// people real?" So the page leads with proof a fake site cannot have:
//
//   1. The showroom, with its address and a Directions button.
//   2. The two people who run Citi Cool, with photos and a LinkedIn profile.
//   3. Official meetings with Daikin's senior management, at our office.
//      Saheb's rules (30 Oct 2026): no names, no location or experience-centre
//      wording, no celebration or cake photos. It should read as one of our
//      regular meetings without the page saying so.
//   4. The showroom gallery, dealer certificates, awards, clients, map.
//
// No year-by-year story (Saheb found it forced) and no "family business"
// wording. Photos live in public/media/about/ as 640 and 1200 px WebP copies.

const SALES = { tel: '+917703900307', text: '77039 00307' };
const SERVICE = { tel: '+919599083770', text: '95990 83770' };
const MAP_QUERY = 'Citi Cool Corporation, 13 Jain Mandir Market, South Extension Part 2, New Delhi 110049';
const DIRECTIONS = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(MAP_QUERY)}`;
const IMG = '/media/about/';

const STATS = [
  { value: '1990', label: 'In business since' },
  { value: '1,500+', label: 'Projects completed' },
  { value: '65', label: 'People in our own install and service team' },
  { value: '4 brands', label: 'Daikin, O-General, Hitachi, Mitsubishi Heavy Industries' },
];

const OWNERS = [
  {
    name: 'Hemant Sareen',
    role: 'Founder & Managing Director',
    text: 'Mechanical engineer. Started Citi Cool in 1990. 35+ years in HVAC: homes, offices and large commercial sites.',
    image: '/media/p3yqcz6s-chatgpt-image-feb-26-2026-12-41-22-pm.webp',
    imageClass: 'object-top',
  },
  {
    name: 'Saheb Sareen',
    role: 'Director, Operations & Strategy',
    text: 'Engineer from Manipal, MBA from NMIMS. Worked in consulting at PwC India.',
    image: '/media/hjgs5aq5-image.webp',
    linkedin: 'https://www.linkedin.com/in/sahebsareen/',
  },
];

const DAIKIN_LARGE = [
  { file: 'daikin-meeting-official', alt: "Official meeting with Daikin's senior management", caption: "Official meeting with Daikin's senior management" },
  { file: 'daikin-meeting-teams', alt: 'The Daikin and Citi Cool teams together', caption: 'The Daikin and Citi Cool teams' },
];
const DAIKIN_SMALL = [
  { file: 'daikin-meeting-table', alt: "Business meeting with Daikin's senior management", caption: 'Business meeting' },
  { file: 'daikin-meeting-presentation', alt: 'Daikin presentation during the meeting', caption: 'Daikin presentation' },
  { file: 'daikin-meeting-discussion', alt: "Discussion with Daikin's senior management", caption: 'Discussion' },
  { file: 'daikin-meeting-vrv-x', alt: "Discussing Daikin VRV X with Daikin's senior management", caption: 'VRV X' },
  { file: 'daikin-meeting-vrv-s', alt: "Showing the Daikin VRV S to Daikin's senior management", caption: 'VRV S' },
  { file: 'daikin-meeting-vrv-home', alt: "Daikin's senior management at the VRV Home display", caption: 'VRV Home' },
];
const SHOWROOM = [
  { file: 'showroom-inside', alt: 'Inside the Citi Cool showroom', caption: 'Inside our showroom' },
  { file: 'showroom-display', alt: 'Daikin cassette, wall and floor units on display', caption: 'Indoor units on display' },
  { file: 'showroom-units', alt: 'Daikin VRV Home and split AC indoor units side by side', caption: 'VRV Home and split ACs side by side' },
  { file: 'showroom-controls', alt: 'Wall of working Daikin remote controls', caption: 'Working remote controls' },
];

const src = (file, w) => `${IMG}${file}-${w}.webp`;
const srcSet = (file) => `${src(file, 640)} 640w, ${src(file, 1200)} 1200w`;

function Photo({ item, sizes, className, zoom = true }) {
  return (
    <figure className="m-0 flex flex-col gap-1.5">
      <img
        src={src(item.file, 1200)}
        srcSet={srcSet(item.file)}
        sizes={sizes}
        alt={item.alt}
        loading="lazy"
        decoding="async"
        data-zoomable={zoom ? 'true' : undefined}
        tabIndex={zoom ? 0 : undefined}
        className={`w-full rounded-xl bg-gray-100 object-cover ${className}`}
      />
      <figcaption className="text-[13px] text-gray-600">{item.caption}</figcaption>
    </figure>
  );
}

function SectionTitle({ children, sub }) {
  return (
    <div className="mb-5">
      <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 lg:text-[28px]">{children}</h2>
      {sub && <p className="mt-1.5 text-[15px] text-gray-600">{sub}</p>}
    </div>
  );
}

const callSales = (source) => (e) => {
  track(FUNNEL.PHONE_CLICK, { source });
  if (window.gtagSendEvent) {
    e.preventDefault();
    window.gtagSendEvent(`tel:${SALES.tel}`);
  }
};

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>About Citi Cool Corporation | Authorised Daikin Dealer, South Extension, Delhi</title>
        <meta name="description" content="Meet the people behind Citi Cool: cooling Delhi since 1990, authorised Daikin dealer since 2003. Our showroom in South Extension Part 2, our own install and service team, dealer certificates and awards." />
      </Helmet>

      <main className="pt-16 lg:pt-20">
        {/* Banner: the showroom, and the facts */}
        <section className="bg-blue-50">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-8 px-4 pb-6 pt-8 sm:px-6 lg:pb-8 lg:pt-12">
            <div className="min-w-0 flex-[1_1_420px]">
              <p className="text-[13px] font-bold uppercase tracking-wider text-blue-700">About Citi Cool</p>
              <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-gray-900 lg:text-[40px] lg:leading-[1.1]">
                Cooling Delhi since 1990.
              </h1>
              <p className="mt-3 text-base leading-relaxed text-gray-700 lg:text-[17px]">
                Authorised Daikin dealer since 2003. Sales and service under one roof, at our showroom in
                South Extension Part 2, New Delhi.
              </p>
              <div className="mt-5 flex flex-wrap gap-2.5">
                <a href={DIRECTIONS} target="_blank" rel="noopener noreferrer"
                  className="inline-flex h-11 items-center gap-2 rounded-lg bg-blue-700 px-5 text-[15px] font-semibold text-white hover:bg-blue-800">
                  <MapPin size={18} aria-hidden="true" /> Get directions
                </a>
                <a href={`tel:${SALES.tel}`} onClick={callSales('about_banner')}
                  className="inline-flex h-11 items-center gap-2 rounded-lg border-2 border-blue-700 bg-white px-5 text-[15px] font-semibold text-blue-700 hover:bg-blue-50">
                  <Phone size={17} aria-hidden="true" /> Call sales: {SALES.text}
                </a>
              </div>
            </div>
            <div className="min-w-0 flex-[1_1_420px]">
              <img
                src={`${IMG}showroom-front-1170.webp`}
                srcSet={`${IMG}showroom-front-640.webp 640w, ${IMG}showroom-front-1170.webp 1170w`}
                sizes="(min-width: 1024px) 540px, 100vw"
                alt="Citi Cool Corporation showroom, Daikin Solution Plaza, South Extension Part 2, New Delhi"
                fetchpriority="high"
                className="aspect-[3/2] w-full rounded-2xl bg-gray-200 object-cover"
              />
            </div>
          </div>
          <div className="mx-auto max-w-6xl px-4 pb-10 sm:px-6">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {STATS.map((s) => (
                <div key={s.label} className="rounded-xl bg-white px-4 py-3.5">
                  <p className="text-2xl font-extrabold text-blue-700">{s.value}</p>
                  <p className="mt-0.5 text-[13px] leading-snug text-gray-600">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The people */}
        <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:py-12">
          <SectionTitle>The people behind Citi Cool</SectionTitle>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {OWNERS.map((o) => (
              <div key={o.name} className="flex items-start gap-4 rounded-xl border border-gray-200 p-[18px]">
                <img src={o.image} alt={o.name} loading="lazy" decoding="async"
                  className={`h-[88px] w-[88px] shrink-0 rounded-full border-4 border-blue-100 object-cover ${o.imageClass || ''}`} />
                <div className="min-w-0">
                  <p className="text-[17px] font-bold text-gray-900">{o.name}</p>
                  <p className="mt-0.5 text-[13px] font-semibold text-blue-700">{o.role}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{o.text}</p>
                  {o.linkedin && (
                    <a href={o.linkedin} target="_blank" rel="noopener noreferrer"
                      className="mt-2 inline-flex h-9 items-center gap-1.5 rounded-lg border border-blue-200 px-3 text-[13px] font-semibold text-blue-700 hover:bg-blue-50">
                      <Linkedin size={14} aria-hidden="true" /> View LinkedIn profile
                    </a>
                  )}
                </div>
              </div>
            ))}
            <div className="flex flex-col gap-2 rounded-xl bg-blue-700 p-[18px] text-white md:col-span-2 lg:col-span-1">
              <p className="text-[17px] font-bold">Our own team fits your AC</p>
              <p className="text-sm leading-relaxed text-blue-100">
                No outside contractors. The people who install your AC also service it.
              </p>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {[['4', 'Senior field engineers'], ['8', 'Technical supervisors'], ['53', 'Trained technicians']].map(([n, l]) => (
                  <div key={l}>
                    <p className="text-2xl font-extrabold">{n}</p>
                    <p className="text-xs leading-snug text-blue-100">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Daikin */}
        <section className="bg-gray-50">
          <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:py-12">
            <SectionTitle>With Daikin&apos;s senior management</SectionTitle>
            <div className="grid gap-3 md:grid-cols-2">
              {DAIKIN_LARGE.map((p) => (
                <Photo key={p.file} item={p} sizes="(min-width: 768px) 540px, 100vw" className="aspect-[16/9]" />
              ))}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {DAIKIN_SMALL.map((p) => (
                <Photo key={p.file} item={p} sizes="(min-width: 1024px) 180px, 50vw" className="aspect-[4/3]" />
              ))}
            </div>
          </div>
        </section>

        {/* Showroom */}
        <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:py-12">
          <SectionTitle sub="Working Daikin units you can see and hear before you buy.">See us in person</SectionTitle>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {SHOWROOM.map((p) => (
              <Photo key={p.file} item={p} sizes="(min-width: 1024px) 270px, 50vw" className="aspect-[4/3]" />
            ))}
          </div>
        </section>

        {/* Certificates and awards */}
        <section id="certificates" className="bg-gray-50">
          <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:py-12">
            <SectionTitle sub="Tap a certificate to read it full size. These are the latest the brands have issued.">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="text-blue-700" size={24} aria-hidden="true" />
                Dealer certificates
              </span>
            </SectionTitle>
            <div className="grid gap-4 md:grid-cols-2">
              {[
                ['/Daikin Authorized Dealership Certificate.webp', 'Daikin authorised dealership certificate 2025-26, Citi Cool Corporation', 'Daikin · Authorised dealer'],
                ['/O-General Authorized Dealership Certificate.webp', 'O-General authorised sales and service dealer certificate, Citi Cool Corporation', 'O-General · Authorised dealer'],
              ].map(([img, alt, label]) => (
                <div key={img} className="rounded-xl border border-gray-200 bg-white p-3">
                  <img src={img} alt={alt} loading="lazy" data-zoomable="true" tabIndex={0} className="w-full rounded-lg" />
                  <p className="mt-2.5 text-[15px] font-bold text-gray-900">{label}</p>
                </div>
              ))}
            </div>

            <h3 className="mt-9 text-lg font-bold text-gray-900">Awards</h3>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {awards.map((a) => (
                <div key={a.id} className="flex flex-col rounded-xl border border-gray-200 bg-white p-3">
                  <div className="flex h-28 items-center justify-center rounded-lg bg-white lg:h-36">
                    {a.image
                      ? <img src={a.image} alt={a.title} loading="lazy" className="max-h-full max-w-full object-contain" />
                      : <Trophy className="text-amber-500" size={44} aria-hidden="true" />}
                  </div>
                  <p className="mt-2 text-sm font-bold leading-snug text-gray-900">{a.title}</p>
                  <p className="mt-0.5 text-xs text-gray-600">{[a.year, a.organization].filter(Boolean).join(' · ')}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Clients */}
        <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:py-12">
          <SectionTitle>Trusted by</SectionTitle>
          <ul className="flex flex-wrap gap-2.5">
            {clients.map((c) => (
              <li key={c.id} className="inline-flex h-10 items-center rounded-lg border border-gray-200 bg-gray-50 px-4 text-sm font-semibold text-gray-700">
                {c.name}
              </li>
            ))}
          </ul>
        </section>

        {/* Visit */}
        <section className="bg-blue-50">
          <div className="mx-auto flex max-w-6xl flex-wrap gap-8 px-4 pb-14 pt-10 sm:px-6 lg:pt-12">
            <div className="flex min-w-0 flex-[1_1_340px] flex-col gap-3">
              <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 lg:text-[28px]">Visit the showroom</h2>
              <p className="text-[15px] leading-relaxed text-gray-700">13 Jain Mandir Market, South Extension Part 2, New Delhi 110049</p>
              <p className="text-sm text-gray-600">Monday to Saturday, 9 am to 6 pm</p>
              <div className="flex flex-col gap-1.5 text-[15px]">
                <a href={`tel:${SALES.tel}`} onClick={callSales('about_visit')} className="font-semibold text-blue-700">Sales: {SALES.text}</a>
                <a href={`tel:${SERVICE.tel}`} onClick={() => track(FUNNEL.PHONE_CLICK, { source: 'about_service' })} className="text-gray-700">
                  Service &amp; repairs: {SERVICE.text}
                </a>
                <a href="mailto:admin@citicool.in" className="text-gray-700">admin@citicool.in</a>
              </div>
              <a href={DIRECTIONS} target="_blank" rel="noopener noreferrer"
                className="mt-1 inline-flex h-11 items-center gap-2 self-start rounded-lg bg-blue-700 px-5 text-[15px] font-semibold text-white hover:bg-blue-800">
                <MapPin size={18} aria-hidden="true" /> Get directions
              </a>
            </div>
            <iframe
              title="Citi Cool showroom on Google Maps"
              src={`https://www.google.com/maps?q=${encodeURIComponent(MAP_QUERY)}&output=embed`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="h-72 min-w-0 flex-[1_1_440px] rounded-2xl border-0 bg-gray-200"
            />
          </div>
        </section>
      </main>
    </>
  );
}
